import {
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    styled,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {getSectionTitle} from '../../../common/constants';
import {BackgroundType, EditRequestDTO, EditTextContentDTO, Section} from '../../../api/models';
import {BackgroundColorPicker} from './background/BackgroundColorPicker';
import {BackgroundImagePicker} from './background/BackgroundImagePicker';
import {useEditForm} from '../../../api';

const BackgroundBoxWrapper = styled(Box)<{backgroundColor: string}>(({backgroundColor, theme}) => ({
    paddingTop: 120,
    paddingLeft: 54,
    paddingRight: 54,
    paddingBottom: 32,
    margin: 0,
    backgroundColor: backgroundColor,

    [theme.breakpoints.down('sm')]: {
        paddingTop: 0,
    },
}));

export interface FormComponentProps {
    data: Section;
    throwSnackbarMessage: (message: string) => void;
    websiteId: string;
    refetch: VoidFunction;
}

export const FormComponent = ({data, websiteId, throwSnackbarMessage, refetch}: FormComponentProps) => {
    const [titleText, updateTitleText] = useState<string>(data.title.text);
    const [contentText, updateContentText] = useState<string | undefined>(data.content?.text);
    const sectionTitle = getSectionTitle(data.sectionType);
    const [open, setOpen] = useState(false);
    const [backgroundState, updateBackgroundState] = useState<BackgroundType | undefined>(undefined);
    const [edits, updateEdits] = useState<EditTextContentDTO[]>([]);
    const theme = useTheme();

    useEffect(() => {
        updateTitleText(data.title.text);
        updateContentText(data.content?.text);
    }, [data]);

    const handleUpdates = (newTextValue: string, textComponentId: string) => {
        const updateContentRequestDTO: EditTextContentDTO = {
            id: textComponentId,
            text: newTextValue,
            sectionId: data.id,
        };
        if (edits.length === 0) {
            updateEdits([updateContentRequestDTO]);
        } else {
            const currentEdits = edits.filter((component) => component.id !== textComponentId);
            currentEdits.push(updateContentRequestDTO);
            updateEdits(currentEdits);
        }
    };

    const {editWebsiteInfo} = useEditForm({
        websiteId: websiteId,
    });

    const updateWebsiteContent = () => {
        const updateStructure: EditRequestDTO = {
            updates: edits,
        };
        editWebsiteInfo({
            data: {
                editRequestDTO: updateStructure,
                cb: refetch,
            },
            throwSnackbarMessage: (errorMessage: string) => throwSnackbarMessage(errorMessage),
        });
        updateEdits([]);
    };

    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const buttonStyles = isMobileScreen
        ? {
              padding: '10px 20px',
              fontSize: '14px',
              borderRadius: '8px',
              width: '100%',
              backgroundColor: '#90909d',
              color: 'white',
              marginTop: '12px',
              overflow: 'hidden',
              textAlign: 'center',
              whitespace: 'normal',
              lineHeight: '1.2',
          }
        : {};
 
    const isBackgroundPresent = data.backgroundInfo !== undefined;
    return (
        <>
            <BackgroundBoxWrapper backgroundColor={'white'}>
                <Grid container>
                    <Grid item md={12} xs={12} sm={12}>
                        <Typography
                            sx={{
                                marginTop: 1,
                                fontWeight: 700,
                                color: 'black',
                                fontFamily: 'Poppins',
                            }}
                            variant='body1'>
                            Edit {sectionTitle} section's content
                        </Typography>
                    </Grid>
                </Grid>
                <Grid style={{marginTop: 12}} container>
                    <Grid item md={12} xs={12} sm={12}>
                        <TextField
                            style={{
                                color: 'black',
                            }}
                            id={data.title.id}
                            label='Title'
                            variant='outlined'
                            fullWidth
                            value={titleText}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                updateTitleText(e.target.value);
                                handleUpdates(e.target.value, data.title.id);
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid style={{marginTop: 12}} container>
                    <Grid item md={12} xs={12} sm={12}>
                        <TextField
                            id={data.content?.id}
                            label='Content'
                            style={{
                                color: 'black',
                            }}
                            variant='outlined'
                            value={contentText}
                            multiline={true}
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (data.content) {
                                    updateContentText(e.target.value);
                                    handleUpdates(e.target.value, data.content.id ? data.content.id : '');
                                }
                            }}
                        />
                    </Grid>
                </Grid>
                <div style={{marginTop: isMobileScreen ? 0 : 10}}>
                    <Button
                        sx={buttonStyles}
                        onClick={() => {
                            if (edits.length === 0) {
                                throwSnackbarMessage('There are no updates made');
                            } else {
                                // make the API call
                                updateWebsiteContent();
                            }
                        }}>
                        <Typography
                            sx={{
                                marginTop: isMobileScreen ? 0 : 1,
                                fontWeight: 500,
                                color: isMobileScreen ? 'white' : 'black',
                                fontFamily: 'Poppins',
                            }}
                            variant='body1'>
                            Save Changes
                        </Typography>
                    </Button>
                </div>
                <br />
                <Grid style={{marginTop: 1}} container>
                    <Grid item md={12} xs={12} sm={12}>
                        <Box sx={{width: 240}}>
                            <FormControl fullWidth>
                                <InputLabel id='background-select-label'>Background</InputLabel>
                                <Select
                                    labelId='background-select-label'
                                    id='background-select'
                                    value={backgroundState}
                                    label='Background'
                                    onChange={(handleChange: SelectChangeEvent) => {
                                        updateBackgroundState(handleChange.target.value as BackgroundType);
                                    }}>
                                    <MenuItem value={undefined}>No Background Selected</MenuItem>
                                    <MenuItem value={'BackgroundColor'}>Background Color</MenuItem>
                                    <MenuItem value={'BackgroundImage'}>Background Image</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
                {backgroundState === 'BackgroundColor' ? (
                    <BackgroundColorPicker
                        sectionId={data.id}
                        isBackgroundPresent={isBackgroundPresent}
                        backgroundInfo={data.backgroundInfo}
                    />
                ) : backgroundState === 'BackgroundImage' ? (
                    <BackgroundImagePicker
                        isBackgroundPresent={isBackgroundPresent}
                        open={open}
                        setOpen={(newState: boolean) => {
                            setOpen(newState);
                        }}
                        backgroundInfo={data.backgroundInfo}
                        sectionId={data.id}
                    />
                ) : null}
            </BackgroundBoxWrapper>
            <Divider
                style={{
                    marginTop: 1,
                }}
            />
        </>
    );
};
